import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../shared/api/AxiosAdmin';
import ChoshiiLogo from '../ChoshiiLogo';
import SmartVideoPlayerGallery from '../pages/Presentation/SmartVideoPlayerAdmin';


type Row = {   
  _id: string;
  createdAt: string;
  location: {
    city: string;
    country: string;
    countryCode?: string;
  };
  hashtags: string[];
  likes: number;
  media: any[];
  user: { _id: string; profilename: string; email?: string | null; picture?: string };
  isHidden: boolean;
  hiddenAt?: string | null;
  isBlockedPost: boolean;
  blockedAt?: string | null;
  blockedReason?: string;
  blockedBy?: string | null;
  status?: string;
  isAdCreative?: boolean;
};

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  'https://api.unydo.com/api';

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '').replace(/\/$/, '');



type Resp = { total: number; hasMore: boolean; items: Row[] };

type PreviewMedia =
  | {
      kind: 'image';
      url: string;
    }
  | {
      kind: 'video';
      url: string;
      videoId?: string | null;
      hlsUrl?: string | null;
      posterUrl?: string | null;
    };

const resolveVideoUrl = (m: any): string => {
  if (m?.hlsUrl) return m.hlsUrl;
  if (m?.videoId) return `${API_ORIGIN}/hls/playlist/${m.videoId}`;
  if (typeof m?.videoUrl === 'string') return m.videoUrl;
  if (typeof m?.url === 'string') return m.url;
  return '';
};

function normalizePreviewMedia(m: any): PreviewMedia | null {
  if (!m) return null;

  // new pipeline video
  if (m.videoId || m.hlsUrl || m.isHls || m.kind === 'video') {
    const url = resolveVideoUrl(m);
    if (!url) return null;

    return {
      kind: 'video',
      url,
      videoId: m.videoId ?? null,
      hlsUrl: m.hlsUrl ?? null,
      posterUrl: m.posterUrl ?? null,
    };
  }

  // legacy string image/video
  if (typeof m === 'string') {
    const kind = mediaKind(m);
    if (kind === 'image') return { kind: 'image', url: m };
    if (kind === 'video') return { kind: 'video', url: m };
    return null;
  }

  const url =
    m.url || m.imageUrl || m.src || m.thumbnailUrl || m.poster || null;

  if (!url) return null;

  const kind = mediaKind(url);
  if (kind === 'image') return { kind: 'image', url };
  if (kind === 'video') {
    return {
      kind: 'video',
      url,
      videoId: m.videoId ?? null,
      hlsUrl: m.hlsUrl ?? null,
      posterUrl: m.posterUrl ?? null,
    };
  }

  return null;
}

function pickPreviewUrl(m: any): { url: string; kind: 'image' | 'video' | 'unknown' } | null {
  if (!m) return null;

  // Cloudflare Stream → always use MP4
  // Cloudflare Stream → use backend HLS proxy (same as Gallery)
if (m.videoId) {
  return {
    url: `${API_ORIGIN}/hls/playlist/${m.videoId}`,
    kind: 'video',
  };
}


  const url =
    typeof m === 'string'
      ? m
      : m.url || m.videoUrl || m.imageUrl || m.src || m.thumbnailUrl || m.poster;

  if (!url) return null;

  const s = String(url).toLowerCase();
  if (s.endsWith('.mp4') || s.endsWith('.webm') || s.endsWith('.mov')) {
    return { url, kind: 'video' };
  }
  if (s.endsWith('.jpg') || s.endsWith('.jpeg') || s.endsWith('.png') || s.endsWith('.gif') || s.endsWith('.webp')) {
    return { url, kind: 'image' };
  }

  return { url, kind: 'unknown' };
}


const VIDEO_BASE = process.env.REACT_APP_PUBLIC_VIDEO_BASE_URL
  || 'https://appvideo-cdn.b-cdn.net';

function mediaUrl(m: any): string | null {
  if (!m) return null;

  //  HLS path
  if (m.hlsPath) {
    return `${VIDEO_BASE}/${m.hlsPath}`;
  }

  //  full URL stored
  if (m.hlsUrl) {
    return m.hlsUrl;
  }

  // Backward compat
  if (m.videoUrl) return m.videoUrl;
  if (m.url) return m.url;

  if (typeof m === 'string') return m;

  return null;
}



function mediaKind(url: string): 'image' | 'video' | 'unknown' {
  const s = url.toLowerCase();

  if (
  s.includes('/hls/playlist/') ||
  s.endsWith('.m3u8') ||
  s.endsWith('.mp4') ||
  s.endsWith('.mov') ||
  s.endsWith('.webm')
) return 'video';

  if (
    s.endsWith('.jpg') ||
    s.endsWith('.jpeg') ||
    s.endsWith('.png') ||
    s.endsWith('.gif') ||
    s.endsWith('.webp')
  ) return 'image';

  return 'unknown';
}



const AdminMediaModeration: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 40;
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerMedia, setViewerMedia] = useState<PreviewMedia[]>([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [viewerTitle, setViewerTitle] = useState<string>('');

const openPresentation = (post: Row, startIndex = 0) => {
  const media = (post.media || [])
    .map(normalizePreviewMedia)
    .filter(Boolean) as PreviewMedia[];

  if (!media.length) return;

  setViewerMedia(media);
  setViewerIndex(Math.max(0, Math.min(startIndex, media.length - 1)));
  setViewerTitle(`${post.user?.profilename || '(unknown)'} • ${post.location?.city || ''}`);
  setViewerOpen(true);
};

const closePresentation = () => {
  setViewerOpen(false);
  setViewerMedia([]);
  setViewerIndex(0);
  setViewerTitle('');
};

const prev = () => setViewerIndex((i) => (i - 1 + viewerMedia.length) % viewerMedia.length);
const next = () => setViewerIndex((i) => (i + 1) % viewerMedia.length);

useEffect(() => {
  if (!viewerOpen) return;
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closePresentation();
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}, [viewerOpen, viewerMedia.length]);


  const load = async (nextOffset = 0) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await adminApi.get<Resp>('/admin/media/all', {
        params: { limit, offset: nextOffset, q: q.trim() || undefined },
      });      

      setRows(res.data.items);
      setTotal(res.data.total);
      setOffset(nextOffset);
    } catch (e) {
      console.error(e);
      setErr('Failed to load media moderation list');
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async (id: string) => {
    const ok = window.confirm('Delete this post? This will remove the media from storage too.');
    if (!ok) return;

    try {
      await adminApi.delete(`/admin/media/${id}`);
      await load(offset);
    } catch (e) {
      console.error(e);
      alert('Delete failed');
    }
  };

  const toggleHidden = async (id: string, nextHidden: boolean) => {
    const ok = window.confirm(nextHidden ? 'Hide this post?' : 'Unhide this post?');
    if (!ok) return;
  
    try {
      await adminApi.post(`/admin/media/admin/${id}/${nextHidden ? 'hide' : 'unhide'}`);
      await load(offset);
    } catch (e) {
      console.error(e);
      alert('Action failed');
    }
  };

  const toggleBlock = async (id: string, nextBlocked: boolean) => {
    const ok = window.confirm(nextBlocked ? 'Block this post (remove from public)?' : 'Unblock this post?');
    if (!ok) return;
  
    try {
      await adminApi.post(`/admin/media/admin/${id}/${nextBlocked ? 'block' : 'unblock'}`);
      await load(offset);
    } catch (e) {
      console.error(e);
      alert('Action failed');
    }
  };
   
  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shellStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    background: 'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
    color: '#f9fafb',
    fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 25px',
    background: 'rgba(2, 6, 23, 0.9)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(148,163,184,0.2)',
  };

  const windowStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 1200,
    borderRadius: 24,
    border: '1px solid rgba(148,163,184,0.3)',
    background: 'linear-gradient(145deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))',
    boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
    padding: '20px 20px 24px',
    position: 'relative',
  };

  return (
    <div style={shellStyle}>
      <header style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
          <ChoshiiLogo animateSheen fontFamily="'Playfair Display', serif" style={{ width: 160 }} padding={0} />
        </div>
        <div style={{ fontSize: 12, color: '#b1b8c7' }}>Media Moderation</div>
      </header>

      <main style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: 24, overflow: 'auto' }}>
        <div style={windowStyle}>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            aria-label="Back to Admin"
            style={{ position: 'absolute', top: 16, left: 16, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#b1b8c7" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>


          <div style={{ textAlign: 'center', marginBottom: 14, marginTop: 12 }}>
            <h1 style={{ fontSize: 24, marginBottom: 6, letterSpacing: -0.03, color: '#b1b8c7' }}>
              Admin – Media Moderation
            </h1>
            <p style={{ fontSize: 13, color: '#b1b8c7' }}>
              {loading ? 'Loading…' : `${total.toLocaleString()} uploads`}
            </p>
          </div>

          {/* Search */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search hashtag, city, country…"
              style={{
                flex: 1,
                borderRadius: 12,
                padding: '10px 12px',
                background: 'rgba(2,6,23,0.6)',
                border: '1px solid rgba(148,163,184,0.25)',
                color: '#e5e7eb',
              }}
            />
            <button
              type="button"
              onClick={() => load(0)}
              style={{
                borderRadius: 12,
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(148,163,184,0.25)',
                color: '#e5e7eb',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              Search
            </button>
          </div>

          {err && (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 10, color: '#fed7aa', background: 'rgba(127,29,29,0.3)', border: '1px solid rgba(248,113,113,0.6)', fontSize: 13 }}>
              {err}
            </div>
          )}

          {/* List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {rows.map((r) => {
const previews = (r.media || [])
  .map(normalizePreviewMedia)
  .filter(Boolean) as PreviewMedia[];
              const date = new Date(r.createdAt);

              return (
                <div
                  key={r._id}
                  style={{
                    borderRadius: 18,
                    border: '1px solid rgba(148,163,184,0.22)',
                    background: 'rgba(2,6,23,0.55)',
                    padding: 14,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.65)',
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    gap: 14,
                    alignItems: 'start',
                  }}
                >
                  {/* Preview */}
                <div
  style={{
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    width: 300,     // space for multiple thumbnails
  }}
>
  {previews.map((p, i) => (
    <div
      key={i}
      onClick={() => openPresentation(r, i)}
      style={{
        width: 140,
        height: 140,
        borderRadius: 14,
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        background: '#000',
        border: '1px solid rgba(148,163,184,0.18)',
      }}
    >
{p.kind === 'image' && (
  <img
    src={p.url}
    alt=""
    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
  />
)}

{p.kind === 'video' && (
  <SmartVideoPlayerGallery
    videoUrl={p.url}
    muted
    showControls={false}
    clickToToggle={false}
    preload="metadata"
    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
  />
)}

      {p.kind === 'video' && (
        <div
          style={{
            position: 'absolute',
            right: 6,
            bottom: 6,
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            fontSize: 11,
            padding: '3px 6px',
            borderRadius: 999,
          }}
        >
          ▶
        </div>
      )}
    

      {/* +N overlay */}
      {i === 3 && previews.length > 4 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontWeight: 800,
            fontSize: 18,
          }}
        >
          +{previews.length - 3}
        </div>
      )}
    </div>
  ))}
</div>

                  {/* Details */}
                  <div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#e5e7eb' }}>
                        {r.user?.profilename || '(unknown)'}
                      </div>
                      <div style={{ fontSize: 12, color: '#b1b8c7' }}>
                        {date.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 12, color: '#b1b8c7' }}>
                        {r.location?.city}, {r.location?.countryCode || r.location?.country}
                      </div>
                      <div style={{ fontSize: 12, color: '#b1b8c7' }}>
                        ❤️ {r.likes}
                      </div>
                    </div>

                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(r.hashtags || []).slice(0, 20).map((h, idx) => (
                        <span
                          key={`${h}-${idx}`}
                          style={{
                            fontSize: 12,
                            color: '#cbd5e1',
                            padding: '3px 8px',
                            borderRadius: 999,
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(148,163,184,0.18)',
                          }}
                        >
                          #{String(h).replace(/^#/, '')}
                        </span>
                      ))}
                      {(r.hashtags || []).length > 20 && (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>
                          +{(r.hashtags || []).length - 20} more
                        </span>
                      )}
                    </div>
                  </div>

{/* Actions */}
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

<button
  type="button"
  onClick={() => openPresentation(r, 0)}
  disabled={!r.media?.length}
  style={{
    borderRadius: 12,
    padding: '10px 12px',
    cursor: r.media?.length ? 'pointer' : 'not-allowed',
    opacity: r.media?.length ? 1 : 0.5,
    fontWeight: 800,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(148,163,184,0.25)',
    color: '#e5e7eb',
  }}
>
  Presentation
</button>

<button
  type="button"
  onClick={() => toggleBlock(r._id, !r.isBlockedPost)}
  style={{
    borderRadius: 12,
    padding: '10px 12px',
    cursor: 'pointer',
    fontWeight: 800,
    color: r.isBlockedPost ? '#86efac' : '#fca5a5',
    background: r.isBlockedPost ? 'rgba(16,185,129,0.18)' : 'rgba(248,113,113,0.18)',
    border: r.isBlockedPost
      ? '1px solid rgba(16,185,129,0.35)'
      : '1px solid rgba(248,113,113,0.35)',
  }}
>
  {r.isBlockedPost ? 'Unblock' : 'Block'}
</button>

  <button
    type="button"
    onClick={() => doDelete(r._id)}
    style={{
      borderRadius: 12,
      padding: '10px 12px',
      cursor: 'pointer',
      fontWeight: 800,
      color: '#fed7aa',
      background: 'rgba(127,29,29,0.25)',
      border: '1px solid rgba(248,113,113,0.5)',
    }}
  >
    Delete
  </button>

  <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right' }}>
    {r._id}
  </div>
</div>

                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
            <button
              type="button"
              onClick={() => load(Math.max(offset - limit, 0))}
              disabled={offset === 0 || loading}
              style={{
                borderRadius: 12,
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(148,163,184,0.25)',
                color: '#e5e7eb',
                cursor: 'pointer',
                opacity: offset === 0 || loading ? 0.5 : 1,
              }}
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => load(offset + limit)}
              disabled={loading || offset + rows.length >= total}
              style={{
                borderRadius: 12,
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(148,163,184,0.25)',
                color: '#e5e7eb',
                cursor: 'pointer',
                opacity: loading || offset + rows.length >= total ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      </main>

     {viewerOpen && viewerMedia.length > 0 && (
  <div
    onClick={closePresentation}
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.75)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: 'min(1100px, 96vw)',
        maxHeight: '92vh',
        borderRadius: 18,
        border: '1px solid rgba(148,163,184,0.25)',
        background: 'rgba(2,6,23,0.95)',
        boxShadow: '0 25px 80px rgba(0,0,0,0.9)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid rgba(148,163,184,0.18)',
          color: '#e5e7eb',
        }}
      >
        <div style={{ fontSize: 12, color: '#b1b8c7' }}>
          {viewerTitle} • {viewerIndex + 1}/{viewerMedia.length}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={prev} style={{ padding: '6px 10px', borderRadius: 10, cursor: 'pointer' }}>
            ←
          </button>
          <button onClick={next} style={{ padding: '6px 10px', borderRadius: 10, cursor: 'pointer' }}>
            →
          </button>
          <button onClick={closePresentation} style={{ padding: '6px 10px', borderRadius: 10, cursor: 'pointer' }}>
            ✕
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 12 }}>
      {(() => {
  const item = viewerMedia[viewerIndex];
  if (!item) return null;

  if (item.kind === 'image') {
    return (
      <img
        src={item.url}
        alt=""
        style={{ maxWidth: '100%', maxHeight: '78vh', borderRadius: 14 }}
      />
    );
  }

  return (
    <SmartVideoPlayerGallery
      videoUrl={item.url}
      muted={false}
      showControls
      clickToToggle
      preload="auto"
      style={{ maxWidth: '100%', maxHeight: '78vh', borderRadius: 14 }}
    />
  );
})()}

      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default AdminMediaModeration;
